import { useReducer, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSnowflakeSessionToken,
  clearSessionTokenCache,
  streamCortexAgentSessionRun,
  createCortexThread,
  clearThreadCache,
} from "../../api/CortexServiceManager";
import {
  addCortexMessage,
  setStreamNotification,
  startCortexStreaming,
  stopCortexStreaming,
} from "../../store/actions/chatbot-actions";
import { CORTEX_ERROR_MESSAGES } from "../../constants/cortexConstants";

// ─── Status Message Normalizer ────────────────────────────────────────────────
//
// Map raw technical strings to plain-English equivalents that
// match the Video Insights Assistant persona.

const RAW_MESSAGE_MAP = {
  "streaming sql from vision_analyst": "Querying your camera data…",
  "generating sql from vision_analyst": "Querying your camera data…",
  "executing sql from vision_analyst": "Running your query…",
  "interpreting results from vision_analyst": "Reading the results…",
  "running data_to_chart": "Visualizing your data…",
};

function normalizeStatusMessage(raw) {
  if (!raw) return null;
  const mapped = RAW_MESSAGE_MAP[raw.trim().toLowerCase()];
  return mapped ?? null;
}

// ─── Local useReducer ─────────────────────────────────────────────────────────

const initialStreamState = {
  id: null,
  processingState: "idle",
  content: "",
  contentBlocks: [],
  isStreaming: false,
  hasError: false,
  errorMessage: "",
  statusLabel: null,
  warnings: [],
};

function streamReducer(state, action) {
  switch (action.type) {
    case "SET_THINKING":
      return { ...state, processingState: "thinking", isStreaming: true, hasError: false, errorMessage: "", content: "", contentBlocks: [] };
    case "SET_RESPONDING":
      return { ...state, processingState: "responding" };
    case "APPEND_CONTENT":
      return { ...state, content: state.content + action.text };
    case "APPEND_CONTENT_BLOCK":
      return { ...state, contentBlocks: [...state.contentBlocks, action.block] };
    case "COMPLETE":
      return { ...state, processingState: "complete", isStreaming: false };
    case "SET_ERROR":
      return { ...state, processingState: "complete", isStreaming: false, hasError: true, errorMessage: action.message };
    case "SET_STATUS_LABEL":
      return { ...state, statusLabel: action.label };
    case "CLEAR_STATUS_LABEL":
      return { ...state, statusLabel: null };
    case "ADD_WARNING":
      return { ...state, warnings: [...state.warnings, action.message] };
    case "RESET":
      return { ...initialStreamState };
    default:
      return state;
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCortexStream() {
  const dispatch = useDispatch();
  const accountId = useSelector((state) => state.sessionReducer.activeSession?.activeSession?.accountId ?? "");

  const [streamingMessage, localDispatch] = useReducer(streamReducer, initialStreamState);

  const abortControllerRef = useRef(null);
  const currentRunIdRef = useRef(0);
  const accumulatedContentRef = useRef("");
  const accumulatedContentBlocksRef = useRef([]);
  const streamCompletedRef = useRef(false);
  const accumulatedWarningsRef = useRef([]);
  const hasReceivedTextDeltaRef = useRef(false);
  const lastMessageIdRef = useRef(0);

  const sendMessage = useCallback(
    async (userText, history) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (!accountId) {
        localDispatch({
          type: "SET_ERROR",
          message: CORTEX_ERROR_MESSAGES.NO_ACCOUNT_ID,
        });
        dispatch(stopCortexStreaming());
        dispatch(
          addCortexMessage({
            id: String(Date.now()),
            role: "assistant",
            content: "",
            timestamp: new Date(),
            processingState: "complete",
            isStreaming: false,
            hasError: true,
            errorMessage: CORTEX_ERROR_MESSAGES.NO_ACCOUNT_ID,
          })
        );
        return;
      }

      const runId = ++currentRunIdRef.current;

      accumulatedContentRef.current = "";
      accumulatedContentBlocksRef.current = [];
      streamCompletedRef.current = false;
      accumulatedWarningsRef.current = [];
      hasReceivedTextDeltaRef.current = false;

      const controller = new AbortController();
      abortControllerRef.current = controller;

      localDispatch({ type: "SET_THINKING" });
      dispatch(startCortexStreaming());

      dispatch(
        addCortexMessage({
          id: String(Date.now()),
          role: "user",
          content: userText,
          timestamp: new Date(),
        })
      );

      // Filter out assistant messages with empty content — Snowflake Agent API
      // rejects conversations containing empty assistant turns.
      const cleanHistory = history.filter(
        (msg) => !(msg.role === "assistant" && (!msg.content || msg.content.trim() === ""))
      );

      const fullMessages = [
        ...cleanHistory,
        { role: "user", content: userText },
      ];

      let reader = null;
      let timeoutId = null;
      let messagesToSend = null;

      /**
       * Reads a ReadableStream of SSE events and dispatches Redux/local actions.
       * Returns true when the stream completes normally, throws on errors.
       */
      const consumeStream = async (streamReader) => {
        const decoder = new TextDecoder();
        let buffer = "";
        let currentEventType = null;

        while (true) {
          let result;
          try {
            result = await streamReader.read();
          } catch (readErr) {
            if (readErr.name === "AbortError" || controller.signal.aborted) {
              dispatch(stopCortexStreaming());
              localDispatch({ type: "COMPLETE" });
              return false; // aborted — caller should return
            }
            throw readErr;
          }

          if (result.done) break;

          buffer += decoder.decode(result.value, { stream: true }).replace(/\r\n/g, "\n");
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            const trimmed = line.trim();

            if (trimmed.startsWith("event:")) {
              currentEventType = trimmed.slice(6).trim();
            } else if (trimmed.startsWith("data:")) {
              const dataStr = trimmed.slice(5).trim();
              if (!currentEventType || dataStr === "[DONE]") continue;

              let data;
              try {
                data = JSON.parse(dataStr);
              } catch {
                continue;
              }

              switch (currentEventType) {
                case "response.thinking.delta":
                  break; // Reasoning events are filtered server-side; no-op for forward compat

                case "response.text.delta":
                case "message.delta": {
                  // message.delta: Anthropic-style alias for response.text.delta
                  const txt = data.text || "";
                  if (!txt) break;
                  hasReceivedTextDeltaRef.current = true;
                  localDispatch({ type: "SET_RESPONDING" });
                  localDispatch({ type: "APPEND_CONTENT", text: txt });
                  accumulatedContentRef.current += txt;
                  break;
                }

                case "response.table": {
                  const hasResultSet = !!data.result_set?.resultSetMetaData?.rowType;
                  const block = hasResultSet
                    ? {
                        type: "table",
                        format: "json",
                        title: data.title || "",
                        columns: data.result_set.resultSetMetaData.rowType.map((r) => r.name),
                        rows: data.result_set.data || [],
                        totalRows: data.result_set.resultSetMetaData.numRows ?? 0,
                      }
                    : {
                        type: "table",
                        format: "markdown",
                        title: data.title || "",
                        markdown: data.markdown || "",
                      };
                  localDispatch({ type: "APPEND_CONTENT_BLOCK", block });
                  accumulatedContentBlocksRef.current = [...accumulatedContentBlocksRef.current, block];
                  break;
                }

                case "response.chart": {
                  const block = { type: "chart", chartSpec: data.chart_spec || "" };
                  localDispatch({ type: "APPEND_CONTENT_BLOCK", block });
                  accumulatedContentBlocksRef.current = [...accumulatedContentBlocksRef.current, block];
                  break;
                }

                case "response.suggested_queries": {
                  // sf-manager 2.1.0 allowlist: suggested follow-up queries
                  if (Array.isArray(data.queries) && data.queries.length > 0) {
                    const block = { type: "suggested_queries", queries: data.queries };
                    localDispatch({ type: "APPEND_CONTENT_BLOCK", block });
                    accumulatedContentBlocksRef.current = [...accumulatedContentBlocksRef.current, block];
                  }
                  break;
                }

                case "response.text.annotation":
                  // sf-manager 2.1.0 allowlist: { doc_title, type, index }
                  // Vision portal does not render document citations — no-op.
                  break;

                case "metadata": {
                  if (data.metadata?.message_id) {
                    // Store as string to preserve precision (JavaScript loses precision > 2^53)
                    lastMessageIdRef.current = String(data.metadata.message_id);
                  }
                  break;
                }

                case "done":
                case "message.stop":
                  // Terminal event aliases — handled by natural stream-end below.
                  break;

                case "error":
                  streamCompletedRef.current = true;
                  dispatch(stopCortexStreaming());
                  localDispatch({ type: "SET_ERROR", message: data.message });
                  dispatch(
                    addCortexMessage({
                      id: String(Date.now()),
                      role: "assistant",
                      content: "",
                      timestamp: new Date(),
                      processingState: "complete",
                      isStreaming: false,
                      hasError: true,
                      // sf-manager 2.1.0 sends a generic message + code; display message only
                      errorMessage: data.message || CORTEX_ERROR_MESSAGES.GENERIC,
                    })
                  );
                  return false; // done — caller should return

                case "response.status": {
                  // sf-manager 2.1.0: carries a server-chosen label; null clears it.
                  if (data.label !== undefined) {
                    if (data.label) localDispatch({ type: "SET_STATUS_LABEL", label: data.label });
                    else localDispatch({ type: "CLEAR_STATUS_LABEL" });
                    break;
                  }
                  // Legacy: map known status keys then fall back to message normalizer
                  const statusLabels = {
                    planning: "Planning response…",
                    orchestrating: "Orchestrating…",
                    reflecting: "Reviewing results…",
                  };
                  const label = statusLabels[data.status] || normalizeStatusMessage(data.message);
                  if (label) localDispatch({ type: "SET_STATUS_LABEL", label });
                  break;
                }

                case "response.tool_use":
                case "response.tool_result":
                  // Filtered by sf-manager 2.1.0 — no-op; kept for forward compat
                  break;

                case "response.tool_result.status": {
                  const label = normalizeStatusMessage(data.message);
                  if (label) localDispatch({ type: "SET_STATUS_LABEL", label });
                  break;
                }

                case "response.warning": {
                  const msg = data.message;
                  if (msg) {
                    localDispatch({ type: "ADD_WARNING", message: msg });
                    accumulatedWarningsRef.current = [...accumulatedWarningsRef.current, msg];
                  }
                  break;
                }

                case "response": {
                  if (!accumulatedContentRef.current && Array.isArray(data.content)) {
                    const textBlock = data.content.find((c) => c.type === "text");
                    if (textBlock?.text) {
                      localDispatch({ type: "SET_RESPONDING" });
                      localDispatch({ type: "APPEND_CONTENT", text: textBlock.text });
                      accumulatedContentRef.current = textBlock.text;
                    }
                  }
                  break;
                }

                default:
                  break;
              }
            }
          }
        }

        return true; // reached result.done naturally
      };

      try {
        const accountname = accountId;

        // sf-manager 2.1.0: session lives in an HttpOnly cookie. ensureSession
        // creates a new session when none is cached or it is within 5 min of expiry.
        await fetchSnowflakeSessionToken(accountname);

        // Create a Cortex Agent thread for this account (reuses cached thread if available)
        const threadId = await createCortexThread(accountname);

        // When using thread_id, Snowflake keeps history server-side. Only send latest message.
        messagesToSend = threadId
          ? [{ role: "user", content: userText }]
          : fullMessages;

        reader = await streamCortexAgentSessionRun({
          messages: messagesToSend,
          signal: controller.signal,
          accountname,
          threadId,
          parentMessageId: lastMessageIdRef.current,
        });

        let lastChunkTime = Date.now();
        timeoutId = setInterval(() => {
          if (Date.now() - lastChunkTime > 60_000) {
            controller.abort();
          }
        }, 5_000);

        // Wrap reader to update lastChunkTime on every chunk (avoids modifying original)
        const origRead = reader.read.bind(reader);
        const wrappedReader = {
          read: async () => {
            const res = await origRead();
            lastChunkTime = Date.now();
            return res;
          },
          releaseLock: () => reader.releaseLock?.(),
        };

        const keepGoing = await consumeStream(wrappedReader);
        if (!keepGoing) return; // aborted, metadata received, or error event

        // Stream ended naturally without a metadata or error event
        if (!streamCompletedRef.current) {
          streamCompletedRef.current = true;
          dispatch(stopCortexStreaming());
          localDispatch({ type: "COMPLETE" });

          if (runId !== currentRunIdRef.current) return;

          dispatch(
            addCortexMessage({
              id: String(Date.now()),
              role: "assistant",
              content: accumulatedContentRef.current,
              timestamp: new Date(),
              contentBlocks: accumulatedContentBlocksRef.current,
              warnings: accumulatedWarningsRef.current,
              sources: [],
              processingState: "complete",
              isStreaming: false,
              hasError: false,
            })
          );

          dispatch(setStreamNotification());
          // Chat history is written server-side via log_history: true.
        }
      } catch (err) {
        if (err.name === "AbortError" || controller.signal.aborted) {
          dispatch(stopCortexStreaming());
          localDispatch({ type: "COMPLETE" });
          return;
        }

        // 401 from the session-run call: the session cookie has expired.
        // Clear the local cache, create a fresh session, and retry once.
        // A second 401 means the session could not be established (e.g., vision
        // agent user not configured) — fall through to the generic error handler.
        if (err.message?.includes("401")) {
          clearSessionTokenCache();
          clearThreadCache();
          try {
            const accountname = accountId;
            await fetchSnowflakeSessionToken(accountname);
            const threadId = await createCortexThread(accountname);

            if (reader) {
              try { reader.releaseLock && reader.releaseLock(); } catch (_) {}
              reader = null;
            }

            reader = await streamCortexAgentSessionRun({
              messages: messagesToSend,
              signal: controller.signal,
              accountname,
              threadId,
              parentMessageId: lastMessageIdRef.current,
            });

            // Reset accumulated state before consuming the retry stream
            accumulatedContentRef.current = "";
            accumulatedContentBlocksRef.current = [];
            streamCompletedRef.current = false;
            accumulatedWarningsRef.current = [];
            hasReceivedTextDeltaRef.current = false;

            const keepGoingRetry = await consumeStream(reader);
            if (!keepGoingRetry) return;

            if (!streamCompletedRef.current) {
              streamCompletedRef.current = true;
              dispatch(stopCortexStreaming());
              localDispatch({ type: "COMPLETE" });
              if (runId !== currentRunIdRef.current) return;
              dispatch(addCortexMessage({ id: String(Date.now()), role: "assistant", content: accumulatedContentRef.current, timestamp: new Date(), contentBlocks: accumulatedContentBlocksRef.current, warnings: accumulatedWarningsRef.current, sources: [], processingState: "complete", isStreaming: false, hasError: false }));
              dispatch(setStreamNotification());
            }
            return;
          } catch (retryErr) {
            if (retryErr.name === "AbortError" || controller.signal.aborted) {
              dispatch(stopCortexStreaming());
              localDispatch({ type: "COMPLETE" });
              return;
            }
            // Second failure — fall through to generic error below
          }
        }

        dispatch(stopCortexStreaming());
        localDispatch({
          type: "SET_ERROR",
          message: CORTEX_ERROR_MESSAGES.GENERIC,
        });
        dispatch(
          addCortexMessage({
            id: String(Date.now()),
            role: "assistant",
            content: "",
            timestamp: new Date(),
            processingState: "complete",
            isStreaming: false,
            hasError: true,
            errorMessage: CORTEX_ERROR_MESSAGES.GENERIC,
          })
        );
      } finally {
        clearInterval(timeoutId);
        if (reader) {
          try {
            reader.releaseLock && reader.releaseLock();
          } catch (_) {}
        }
      }
    },
    [dispatch, accountId]
  );

  const stopStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    dispatch(stopCortexStreaming());
    localDispatch({ type: "COMPLETE" });
  }, [dispatch]);

  return { streamingMessage, sendMessage, stopStream };
}
